---
title: "Fibonacci Retracements and Extensions"
slug: fibonacci-retracements-extensions
shortTitle: "Fibonacci Levels"
description: "How Fibonacci retracement and extension levels are calculated from a price swing, how traders use them for entries and targets, and why they need other evidence."
level: advanced
category: trading
course: advanced
order: 3
topics: [technical-analysis, indicators]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/fibonacci-retracements-extensions.svg
faqs:
  - q: "Why would Fibonacci levels work at all?"
    a: "There is no proof that markets follow these ratios. Levels may matter partly because many traders watch the same ones, and partly because pullbacks of roughly a third to two thirds of a move are common anyway. Treat them as reference zones, not rules."
  - q: "Is 50% a Fibonacci ratio?"
    a: "Not strictly. It does not come from the Fibonacci sequence, but it is included on almost every Fibonacci tool because traders widely watch halfway pullbacks."
  - q: "Should I measure from candle wicks or candle bodies?"
    a: "Most traders use the wicks, the true high and low of the swing, but either works if you stay consistent. Mixing the two between charts makes your levels hard to compare."
  - q: "Which Fibonacci level is the best place to enter?"
    a: "None is reliably better. Many traders watch the zone between 38.2% and 61.8% and look for a level there that lines up with support, resistance or structure, then wait for price to react before entering."
  - q: "Does {{BRAND}} have a Fibonacci tool on its chart?"
    a: "The trade chart does not include drawing tools. You can work the levels out from the swing high and low with the formulas in this lesson, or use a separate charting tool, and then place orders on the platform."
---

**Fibonacci retracements and extensions** are horizontal levels drawn from a price swing at fixed ratios. Retracements, at 23.6%, 38.2%, 50%, 61.8% and 78.6% of the swing, mark zones where a pullback might pause. Extensions, such as 127.2% and 161.8%, project beyond the swing and are often used as targets.

The ratios come from a famous number sequence, and that origin gives them an air of precision they do not deserve. Markets do not follow mathematics. Fibonacci levels are useful as a consistent way to measure pullbacks and plan targets, and they carry more weight when they line up with other evidence. This lesson shows the calculations step by step, a worked trade in USDT, and how to use the levels on {{BRAND}}.

<CTA />

<KeyTakeaways>

- **Retracements measure pullbacks.** Levels at 23.6%, 38.2%, 50%, 61.8% and 78.6% of a swing mark zones where a pullback might pause.
- **Extensions project targets.** Levels such as 127.2% and 161.8% extend beyond the swing and are often used for take-profit ideas.
- **The swing you choose decides everything.** Different anchor points give different levels, so pick clear swings on the timeframe you trade.
- **Confluence beats a lone level.** A level matters more when it lines up with support, resistance or structure; on its own it is just a number.

</KeyTakeaways>

## Where the numbers come from

The Fibonacci sequence starts 0, 1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, with each number the sum of the two before it. Divide any number by the next one and you approach 0.618 (55 ÷ 89 ≈ 0.618). Divide by the number two places on and you approach 0.382 (34 ÷ 89 ≈ 0.382), and three places on gives about 0.236. The extension ratios come from the same family: 1.618 is the inverse of 0.618, and 1.272 is its square root.

Two popular levels are not Fibonacci ratios at all. **50%** is included because halfway pullbacks are widely watched, and **78.6%** is the square root of 0.618. None of this proves the levels work. A likelier explanation is that many traders watch them, and that pullbacks of between a third and two thirds of a move are common anyway.

## Calculating retracements

Pick a completed swing. In an uptrend, measure from the swing low to the swing high; the retracement levels sit below the high:

**Level = swing high − (swing high − swing low) × ratio**

In a downtrend, measure from the swing high to the swing low, and the levels sit above the low: **Level = swing low + (swing high − swing low) × ratio**.

<CompareTable caption="Retracement levels for a BTC swing from 56,000 to 64,000 (illustrative prices)">

| Ratio | Calculation | Level |
| --- | --- | --- |
| 23.6% | 64,000 − 8,000 × 0.236 = 64,000 − 1,888 | 62,112 |
| 38.2% | 64,000 − 8,000 × 0.382 = 64,000 − 3,056 | 60,944 |
| 50% | 64,000 − 8,000 × 0.5 = 64,000 − 4,000 | 60,000 |
| 61.8% | 64,000 − 8,000 × 0.618 = 64,000 − 4,944 | 59,056 |
| 78.6% | 64,000 − 8,000 × 0.786 = 64,000 − 6,288 | 57,712 |

</CompareTable>

The range of the swing is 64,000 − 56,000 = 8,000, and every level is that range times a ratio, subtracted from the high.

## Calculating extensions

Extensions are measured in two common ways, and charting tools label them differently, so check which one yours uses.

- **From the swing.** Add the range times the ratio to the swing low. For the same swing: 127.2% is 56,000 + 8,000 × 1.272 = 56,000 + 10,176 = **66,176**, and 161.8% is 56,000 + 12,944 = **68,944**.
- **From the pullback.** Wait for the pullback to end, then project the original swing from that point. If the pullback stops at 60,000, the 100% projection is 60,000 + 8,000 = **68,000**, and the 161.8% projection is 60,000 + 12,944 = **72,944**.

Extensions are guides for where to consider taking profit, not forecasts. Price can stop well short of them or run far past.

## Choosing the swing

Fibonacci levels are only as good as the swing you anchor them to. Use clear, obvious highs and lows on the timeframe you trade, the kind other traders would pick too. Be consistent about using candle wicks or bodies. When price makes a new high, the swing has changed, so redraw.

The weakness is that two traders can anchor to different swings and get different levels. If you find yourself moving the anchors until the levels match where price turned, you are fitting the tool to the past, not preparing for the future.

<ProTip>

Look for **confluence**: a Fibonacci level that lines up with a former resistance level, a round number or a structure point. A 50% retracement that also sits on old resistance is more interesting than a 61.8% level in open space. [Support and Resistance](/academy/article/support-and-resistance) shows how to find those levels.

</ProTip>

## A worked trade

<Example title="Buying a pullback on BTC/USDT (illustrative prices)">

BTC has rallied from 56,000 to 64,000 and is pulling back. The 50% retracement at **60,000** is also a round number and an old resistance level from earlier in the rally.

- **Entry.** A Buy Limit at 60,000.
- **Stop.** 58,800, below the 61.8% level at 59,056 with some room. Risk per BTC: 60,000 − 58,800 = **1,200**.
- **Target.** The swing high at 64,000. Reward per BTC: **4,000**, about 3.3 to 1.
- **Size.** With 60 USDT at risk: 60 ÷ 1,200 = **0.05 BTC**, a position worth 0.05 × 60,000 = 3,000 USDT.
- **Outcomes.** At the target: 4,000 × 0.05 = **+200 USDT**. At the stop: 1,200 × 0.05 = **−60 USDT**. Both before costs.

</Example>

A position has one take profit. If you want a second target, say the 127.2% extension at 66,176, you can open the trade as two positions of 0.025 BTC with different TP prices, or close part on desktop with the 25% and 50% buttons in Instant Trade. [Scaling In/Out, Trailing Stops and Partial Take-Profit](/academy/article/scaling-trailing-stops-partial-take-profit) covers both.

<Warning>

A Fibonacci level is not a floor or a ceiling. Price often slices through every level in a strong move, and a limit order at a level can fill just before the market keeps falling. Always set a stop loss, keep the risk per trade small, and remember that leverage magnifies the loss if the level fails.

</Warning>

## Using Fibonacci on {{BRAND}}

<Steps>
<Step title="Read the swing high and low">

Turn on the OHLC legend in the chart settings (the gear icon) and hover over the swing candles to read their exact high and low.

</Step>
<Step title="Work out the levels">

Apply the formulas above, or use the Fibonacci tool in a separate charting tool, since the trade chart has no drawing tools.

</Step>
<Step title="Place the order">

On the **Limit / Stop** tab, a buy price below the Ask becomes a **Buy Limit** and a sell price above the Bid becomes a **Sell Limit**. Turn on **TP / SL** and enter your stop and target prices.

</Step>
<Step title="Adjust as the swing changes">

On desktop, drag the pending order or its TP / SL lines on the chart if a new swing changes your levels. Remember that pending orders reserve margin as soon as they are placed.

</Step>
</Steps>

{/* TODO: verify with platform — that the trade chart has no drawing or Fibonacci tools, the OHLC legend setting name, and that partial closes are only available through Instant Trade's 25% / 50% buttons on desktop. */}

## Common mistakes

- **Forcing Fibonacci onto every chart.** Without a clear swing, the levels are arbitrary.
- **Moving the anchors after the fact.** Pick the swing before you look for reactions.
- **Treating levels as exact.** Think in zones; price often turns a little before or after a level.
- **Ignoring the trend.** Retracements are built for pullbacks within a trend. In a choppy range, they mean much less. [Price Action Trading Without Indicators](/academy/article/price-action-trading) covers reading structure first.

## Conclusion

Fibonacci retracements and extensions turn a price swing into a set of reference levels: retracements for pullback entries, extensions for targets. The arithmetic is simple, the range times a ratio, added or subtracted. What matters more is choosing clear swings, treating levels as zones and looking for confluence with support, resistance and structure. The ratios have no special power over markets, so use them to plan entries, stops and targets, and let your stop loss handle the many times a level fails.
